import { Button } from '@creditornot/cb-button';
import { FormRow, InputContainer } from '@creditornot/cb-form';
import { List } from '@creditornot/cb-icons';
import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { ModalHeader, ModalContent, ModalFooter } from '@creditornot/cb-modal';
import { useMutation } from '@tanstack/react-query';
import React, { useCallback, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { WoltModal } from 'common/components';
import ErrorToastContent from 'common/components/ErrorToastContent';
import { MultiLevelSelect } from 'modules/menu/components/MultiLevelSelect';
import { useBulkChangeCategoryTracking } from 'modules/menu/hooks/telemetryHooks';
import { batchChangeCategory } from 'modules/shared-menu/api';
import { useSelectionData } from 'modules/shared-menu/hooks/useSelectionData';
import { BatchCategoryChangePayload } from 'modules/shared-menu/types';

import ActionButton from './ActionButton';
import { BulkActionModalTitle } from './BulkActionModalTitle';
import { useWoltSpecials } from '../../../context/WoltSpecialsContext';
import { getCategoryOptions } from '../../OfferDetailsForm/utils';

type BulkChangeCategoryProps = {
  selectedIds: Set<string>;
  hasWoltSpecialInSelectedIds: boolean;
};

const Root = styled(WoltModal)`
  width: calc(100vw - 40px);
  min-width: 320px;
  max-width: 640px;
  padding: 0;
  overflow-y: visible;
`;

const StyledModalHeader = styled(ModalHeader)`
  background-color: ${cbSemanticCssVars.surfaceMain};
`;

const StyledModalContent = styled(ModalContent)`
  padding: 0;
  background-color: ${cbSemanticCssVars.surfaceMain};
`;

const StyledModalFooter = styled(ModalFooter)`
  background-color: ${cbSemanticCssVars.surfaceMain};
  display: flex;
  justify-content: flex-end;
  align-items: center;
`;

const CancelButton = styled(Button).attrs({ variant: 'lightBlue', size: 'small' })`
  margin-inline-end: 16px;
`;
const SubmitButton = styled(Button).attrs({ variant: 'blue', size: 'small' })``;

export const BulkChangeCategory = ({ selectedIds, hasWoltSpecialInSelectedIds }: BulkChangeCategoryProps) => {
  const intl = useIntl();
  const [isChangeCategoryModalOpen, setIsChangeCategoryModalOpen] = useState(false);
  const { updateSelectionData, selection } = useSelectionData();
  const [destinationCategoryId, setDestinationCategoryId] = useState<string | null>(null);
  const { categoriesWithoutWoltSpecials } = useWoltSpecials();

  const trackChangeCategory = useBulkChangeCategoryTracking([...selectedIds], destinationCategoryId ?? '');

  const { mutate: executeBatchChangeCategory, isLoading } = useMutation(
    async (payload: BatchCategoryChangePayload) => batchChangeCategory(selection.id, payload),
    {
      onSuccess: updatedSelection => {
        trackChangeCategory();
        updateSelectionData({ actionType: 'updateSelection', payload: updatedSelection });

        toast.success(<FormattedMessage id="toast.success" />);
      },
      onSettled: () => {
        setIsChangeCategoryModalOpen(false);
      },
      onError: error => {
        toast.error(<ErrorToastContent error={error} />);
      },
    },
  );

  const handleOnConfirm = useCallback(() => {
    const payload: BatchCategoryChangePayload = {
      offer_ids: [...selectedIds],
      destination_category_id: destinationCategoryId,
    };

    executeBatchChangeCategory(payload);
  }, [destinationCategoryId, executeBatchChangeCategory, selectedIds]);

  const categoryOptions = useMemo(
    () =>
      getCategoryOptions({ categories: categoriesWithoutWoltSpecials, primaryLanguage: selection.primary_language }),
    [categoriesWithoutWoltSpecials, selection.primary_language],
  );

  const onClose = useCallback(() => setIsChangeCategoryModalOpen(false), []);

  return (
    <>
      <ActionButton
        icon={<List />}
        onClick={() => setIsChangeCategoryModalOpen(true)}
        tooltipTitle={
          hasWoltSpecialInSelectedIds ? (
            <FormattedMessage id="menu.bulkActions.categoryChange.disabledLabel" />
          ) : (
            <FormattedMessage id="menu.bulkActions.categoryChange.title" />
          )
        }
        disabled={hasWoltSpecialInSelectedIds}
      />

      {isChangeCategoryModalOpen && (
        <Root isOpen onRequestClose={onClose}>
          <StyledModalHeader onCloseClick={onClose}>
            <BulkActionModalTitle
              count={selectedIds.size}
              title={<FormattedMessage id="menu.bulkActions.categoryChange.title" />}
            />
          </StyledModalHeader>

          <StyledModalContent>
            <FormRow label={intl.formatMessage({ id: 'menu-item.category' })} labelAlign="center" horizontalPadding>
              <InputContainer>
                <MultiLevelSelect
                  caretPosition="right"
                  value={destinationCategoryId}
                  onChange={setDestinationCategoryId}
                  options={categoryOptions}
                  placeholder={intl.formatMessage({ id: 'menu.selectOrSearch' })}
                />
              </InputContainer>
            </FormRow>
          </StyledModalContent>
          <StyledModalFooter>
            <CancelButton onClick={onClose} disabled={isLoading}>
              <FormattedMessage id="state.cancel" />
            </CancelButton>
            <SubmitButton
              disabled={!destinationCategoryId || isLoading}
              loading={isLoading}
              type="submit"
              onClick={handleOnConfirm}
            >
              <FormattedMessage id="state.save" />
            </SubmitButton>
          </StyledModalFooter>
        </Root>
      )}
    </>
  );
};
